import { hasTierAccess } from '@core/auth';

import InvestorCategoryEnum from '@constants/enums/investor/category';
import UserStatusEnum from '@constants/enums/user/status';
import ViewingRestrictionStatusEnum from '@constants/enums/viewingRestrictionStatus';

import i18n from '@plugins/i18n';

import { Collection, Investor } from '@/models';

export class AdviserClientCollection extends Collection {
    static type = 'AdviserClientCollection';
    static endpoint = Investor.api.index;
    static model = Investor;

    get preset_filters() {
        let filters = {};

        if (this.Auth().is_adviser) {
            filters.include_statistics = true;
        }

        return filters;
    }

    get pdf_headers() {
        return [
            'title',
            'name',
            'gi_ref',
            'email',
            'status',
            'portfolio_restriction_status',
            'category',
            'advisers',
            'total_proposals',
            'proposals_amount',
            'current_value',
            'cash_balance'
        ];
    }

    get headers() {
        const auth = this.Auth();

        const isGrowthInvest = auth.is_gi;
        const isAdviser = auth.is_adviser;
        const isFundManagerOrProvider = auth.is_fund_manager || auth.is_provider;
        const organisationId = auth.organisation_id;

        return [
            this.createHeader('title', i18n.t('title'), {
                width: '7rem',
                sortable: true,
                filterable: {
                    operators: this.getOperators(['contains', 'eq', 'is_empty'])
                }
            }),
            this.createHeader('name', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: true,
                sticky: true
            }),
            this.createHeader('gi_ref', i18n.t('client_reference'), {
                filterable: true,
                width: '11rem'
            }),
            this.createHeader('email', i18n.t('email'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: true
            }),
            this.createHeader('status', i18n.t('status'), {
                filterable: true,
                width: '10rem',
                format: 'enum',
                enum: UserStatusEnum,
                enumA11yKey: 'enums.user_status',
                enumTypes: {
                    [UserStatusEnum.ACTIVE]: 'success',
                    [UserStatusEnum.INACTIVE]: 'grey',
                    [UserStatusEnum.SUSPENDED]: 'error',
                    [UserStatusEnum.UNVERIFIED]: 'warning'
                }
            }),
            this.createHeader('portfolio_restriction_status', i18n.t('restriction_status'), {
                active: !isFundManagerOrProvider,
                filterable: true,
                width: '11rem',
                format: 'enum',
                enum: ViewingRestrictionStatusEnum,
                enumA11yKey: 'enums.viewing_restriction_status',
                enumTypes: {
                    [ViewingRestrictionStatusEnum.NONE]: 'success',
                    [ViewingRestrictionStatusEnum.WARNING]: 'warning',
                    [ViewingRestrictionStatusEnum.RESTRICTED]: 'error'
                },
                formatOptions: {
                    tooltip: v => i18n.t(`enums.viewing_restriction_description.${v}`)
                }
            }),
            this.createHeader('category', i18n.t('investor_type'), {
                filterable: true,
                width: '10rem',
                format: 'enum',
                enum: InvestorCategoryEnum,
                enumA11yKey: 'enums.investor_category'
            }),
            this.createHeader('advisers', i18n.t('advisers'), {
                active: !isFundManagerOrProvider || (isFundManagerOrProvider && hasTierAccess(auth.organisation, 2)),
                width: isGrowthInvest ? '30rem' : '20rem',
                filterable: {
                    component: () => import('@/components/fields/AdviserClientsQuickFilter.vue'),
                    quickFilterTargets: ['adviser_id', 'primary_contact_id', 'authorised'],
                    hideInPanel: true
                },
                transform: data => {
                    const adviserList = [];

                    (data.item.adviser_accounts || []).forEach(aa => {
                        try {
                            let adviserString = '';

                            if (aa.adviser.name) {
                                adviserString = aa.adviser.name;

                                if (aa.primary_contact?.name) {
                                    adviserString += ` (${aa.primary_contact.name})`;
                                }
                            }

                            if (adviserString) {
                                adviserList.push(adviserString);
                            }
                        } catch (error) {
                            //
                        }
                    });

                    return adviserList.join(', ') || null;
                }
            }),
            this.createHeader('adviser_id', i18n.t('adviser'), {
                active: false,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/Adviser.vue'),
                    hideInPanel: !isGrowthInvest && isAdviser
                }
            }),
            this.createHeader('primary_contact_id', i18n.t('primary_contact'), {
                active: false,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/User.vue'),
                    filtersFromContext: filters => {
                        const adviserFilter = filters.find(f => f.column === 'adviser_id');
                        const hasExplicitAdviser = adviserFilter?.criteria != null && adviserFilter.criteria !== '';
                        const useOrganisationDefault = isAdviser && !isGrowthInvest;

                        const adviserId = hasExplicitAdviser
                            ? adviserFilter.criteria
                            : useOrganisationDefault
                            ? organisationId
                            : null;

                        if (adviserId == null) {
                            return {};
                        }
                        return {
                            role_in_organisation: adviserId,
                            primary_contact: `${this.filterOperator('is')}:true`
                        };
                    },
                    disabled: filters => {
                        if (isGrowthInvest) {
                            const hasAdviserFilter = filters.some(
                                f => f.column === 'adviser_id' && f.criteria != null && f.criteria !== ''
                            );
                            return !hasAdviserFilter;
                        }
                        return false;
                    },
                    description: filters => {
                        if (isGrowthInvest) {
                            const hasAdviserFilter = filters.some(
                                f => f.column === 'adviser_id' && f.criteria != null && f.criteria !== ''
                            );

                            if (hasAdviserFilter) {
                                return null;
                            }

                            return i18n.t('select_an_adviser_to_enable_filtering_by_primary_contact');
                        }
                        return null;
                    }
                }
            }),
            this.createHeader('authorised', i18n.t('authorised'), {
                active: false,
                format: 'boolean',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: 'EditBoolean'
                }
            }),
            this.createHeader('total_proposals', i18n.t('total_proposed'), {
                active: !isFundManagerOrProvider,
                filterable: false,
                width: '10rem',
                format: 'number',
                to: data => ({
                    name: 'Proposals',
                    query: { investor_id: data.item.id }
                })
            }),
            this.createHeader('proposals_amount', i18n.t('proposed_amount') + ' (incl fees)', {
                active: !isFundManagerOrProvider,
                filterable: false,
                width: '10rem',
                format: 'money'
            }),
            this.createHeader('current_value', i18n.t('current_value'), {
                active: !isFundManagerOrProvider,
                filterable: false,
                sortable: false,
                width: '10rem',
                format: 'money'
            }),
            this.createHeader('cash_balance', i18n.t('platform_cash_balance'), {
                active: !isFundManagerOrProvider,
                filterable: false,
                width: '10rem',
                format: 'money'
            })
        ];
    }
}

export default AdviserClientCollection;
